import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "~/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-xs px-2 py-1 text-xs font-medium transition-colors duration-fast motion-reduce:transition-none forced-colors:border forced-colors:border-current",
  {
    variants: {
      tone: {
        success: "bg-success-soft text-success-ink",
        info: "bg-info-soft text-info-ink",
        processing: "bg-info-soft text-info-ink",
        warning: "bg-warning-soft text-warning-ink",
        error: "bg-error-soft text-error-ink",
        paused: "bg-neutral-soft text-warning-ink",
        neutral: "bg-neutral-soft text-neutral-ink",
      },
      variant: {
        solid: "",
        soft: "",
        outline: "bg-transparent border border-current",
      },
      size: {
        sm: "text-xs px-1.5 py-0.5 h-6",
        md: "text-sm px-2 py-1 h-7",
      },
    },
    compoundVariants: [
      // Solid variants use solid background tokens with white text
      {
        tone: "success",
        variant: "solid",
        class: "bg-success-solid text-white",
      },
      {
        tone: "info",
        variant: "solid",
        class: "bg-info-solid text-white",
      },
      {
        tone: "processing",
        variant: "solid",
        class: "bg-info-solid text-white",
      },
      {
        tone: "warning",
        variant: "solid",
        class: "bg-warning-solid text-white",
      },
      {
        tone: "error",
        variant: "solid",
        class: "bg-error-solid text-white",
      },
      {
        tone: "paused",
        variant: "solid",
        class: "bg-neutral-solid text-white",
      },
      {
        tone: "neutral",
        variant: "solid",
        class: "bg-neutral-solid text-white",
      },
      // Outline variants use ink tokens for both border and text
      {
        tone: "success",
        variant: "outline",
        class: "border-success-ink text-success-ink",
      },
      {
        tone: "info",
        variant: "outline",
        class: "border-info-ink text-info-ink",
      },
      {
        tone: "processing",
        variant: "outline",
        class: "border-info-ink text-info-ink",
      },
      {
        tone: "warning",
        variant: "outline",
        class: "border-warning-ink text-warning-ink",
      },
      {
        tone: "error",
        variant: "outline",
        class: "border-error-ink text-error-ink",
      },
      {
        tone: "paused",
        variant: "outline",
        class: "border-warning-ink text-warning-ink",
      },
      {
        tone: "neutral",
        variant: "outline",
        class: "border-neutral-ink text-neutral-ink",
      },
    ],
    defaultVariants: {
      tone: "neutral",
      variant: "soft",
      size: "md",
    },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({
  className,
  tone,
  variant,
  size,
  children,
  ...props
}: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, variant, size }), className)} {...props}>
      {children}
    </span>
  );
}

export { badgeVariants };
